// app/components/terms-and-conditions/TermsAndConditions.tsx
"use client";

import * as React from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Box } from "@mui/material";
import { useTranslations, useLocale } from "next-intl";
import { usePathname } from "next/navigation";

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string;
};

export default function TermsAndConditions() {
  const t = useTranslations("TermsAndConditions.Content");
  const locale = useLocale();
  const pathname = usePathname() || "/";

  // map pathname → path สำหรับ CMS (ตัด /en, /zh ออก)
  const pagePath = React.useMemo(() => {
    const parts = pathname.split("/");
    if (parts[1] === "en" || parts[1] === "zh") {
      const tail = parts.slice(2).join("/");
      return tail ? `/${tail}` : "/";
    }
    return pathname || "/";
  }, [pathname]);

  // ---- default text จาก i18n (ดีต่อ SEO / SSR) ----
  const defaultGeneralTitle = t("generalTitle");
  const defaultGeneralContent = t("generalContent");
  const defaultCopyrightTitle = t("copyrightTitle");
  const defaultCopyrightContent = t("copyrightContent");
  const defaultTrademarksTitle = t("trademarksTitle");
  const defaultTrademarksContent = t("trademarksContent");
  const defaultContentTitle = t("contentTitle");
  const defaultContentContent = t("contentContent");
  const defaultLinkedWebsitesTitle = t("linkedWebsitesTitle");
  const defaultLinkedWebsitesContent = t("linkedWebsitesContent");
  const defaultPrivacyPolicyTitle = t("privacyPolicyTitle");
  const defaultPrivacyPolicyContent = t("privacyPolicyContent");

  // ---- state ที่จะแสดงจริง (จะถูก override ด้วย inline edits ถ้ามี) ----
  const [generalTitle, setGeneralTitle] = React.useState(defaultGeneralTitle);
  const [generalContent, setGeneralContent] = React.useState(defaultGeneralContent);
  const [copyrightTitle, setCopyrightTitle] =
    React.useState(defaultCopyrightTitle);
  const [copyrightContent, setCopyrightContent] =
    React.useState(defaultCopyrightContent);
  const [trademarksTitle, setTrademarksTitle] =
    React.useState(defaultTrademarksTitle);
  const [trademarksContent, setTrademarksContent] =
    React.useState(defaultTrademarksContent);
  const [contentTitle, setContentTitle] =
    React.useState(defaultContentTitle);
  const [contentContent, setContentContent] =
    React.useState(defaultContentContent);
  const [linkedWebsitesTitle, setLinkedWebsitesTitle] =
    React.useState(defaultLinkedWebsitesTitle);
  const [linkedWebsitesContent, setLinkedWebsitesContent] =
    React.useState(defaultLinkedWebsitesContent);
  const [privacyPolicyTitle, setPrivacyPolicyTitle] =
    React.useState(defaultPrivacyPolicyTitle);
  const [privacyPolicyContent, setPrivacyPolicyContent] =
    React.useState(defaultPrivacyPolicyContent);

  // reset เมื่อ locale เปลี่ยน
  React.useEffect(() => {
    setGeneralTitle(defaultGeneralTitle);
    setGeneralContent(defaultGeneralContent);
    setCopyrightTitle(defaultCopyrightTitle);
    setCopyrightContent(defaultCopyrightContent);
    setTrademarksTitle(defaultTrademarksTitle);
    setTrademarksContent(defaultTrademarksContent);
    setContentTitle(defaultContentTitle);
    setContentContent(defaultContentContent);
    setLinkedWebsitesTitle(defaultLinkedWebsitesTitle);
    setLinkedWebsitesContent(defaultLinkedWebsitesContent);
    setPrivacyPolicyTitle(defaultPrivacyPolicyTitle);
    setPrivacyPolicyContent(defaultPrivacyPolicyContent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  // โหลด inline edits จาก CMS
  React.useEffect(() => {
    const fetchInline = async () => {
      try {
        const res = await fetch(
          `/api/admin/page-inline?websiteId=${process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID}&path=${encodeURIComponent(
            pagePath
          )}&locale=${locale}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;
        const map: Record<string, string> = {};

        list.forEach((e) => {
          if (e.cmsKey) {
            map[e.cmsKey] = e.value;
            return;
          }
          const match = e.selector?.match(/data-cms-key="([^"]+)"/);
          if (match) {
            map[match[1]] = e.value;
          }
        });

        if (map["terms.generalTitle"])
          setGeneralTitle(map["terms.generalTitle"]);
        if (map["terms.generalContent"])
          setGeneralContent(map["terms.generalContent"]);
        if (map["terms.copyrightTitle"])
          setCopyrightTitle(map["terms.copyrightTitle"]);
        if (map["terms.copyrightContent"])
          setCopyrightContent(map["terms.copyrightContent"]);
        if (map["terms.trademarksTitle"])
          setTrademarksTitle(map["terms.trademarksTitle"]);
        if (map["terms.trademarksContent"])
          setTrademarksContent(map["terms.trademarksContent"]);
        if (map["terms.contentTitle"])
          setContentTitle(map["terms.contentTitle"]);
        if (map["terms.contentContent"])
          setContentContent(map["terms.contentContent"]);
        if (map["terms.linkedWebsitesTitle"])
          setLinkedWebsitesTitle(map["terms.linkedWebsitesTitle"]);
        if (map["terms.linkedWebsitesContent"])
          setLinkedWebsitesContent(map["terms.linkedWebsitesContent"]);
        if (map["terms.privacyPolicyTitle"])
          setPrivacyPolicyTitle(map["terms.privacyPolicyTitle"]);
        if (map["terms.privacyPolicyContent"])
          setPrivacyPolicyContent(map["terms.privacyPolicyContent"]);
      } catch (err) {
        console.warn("[TermsAndConditions] inline fetch error:", err);
      }
    };

    fetchInline();
  }, [locale, pagePath]);

  return (
    <Box className="policy-wrap" sx={{
      py: 10
    }}>
      <Container className="container">
        <Typography
          variant="h4"
          data-aos="fade-up"
          data-cms-key="terms.generalTitle"
          sx={{
            mb: 3
          }}
        >
          {generalTitle}
        </Typography>
        <Typography
          variant="body1"
          data-aos="fade-up"
          data-cms-key="terms.generalContent"
          sx={{
            mb: 3
          }}
        >
          {generalContent}
        </Typography>

        <Typography
          variant="h4"
          data-aos="fade-up"
          data-cms-key="terms.copyrightTitle"
          sx={{
            mb: 3
          }}
        >
          {copyrightTitle}
        </Typography>
        <Typography
          variant="body1"
          data-aos="fade-up"
          data-cms-key="terms.copyrightContent"
          sx={{
            mb: 3,
            whiteSpace: "pre-line"
          }}>
          {copyrightContent}
        </Typography>

        <Typography
          variant="h4"
          data-aos="fade-up"
          data-cms-key="terms.trademarksTitle"
          sx={{
            mb: 3
          }}
        >
          {trademarksTitle}
        </Typography>
        <Typography
          variant="body1"
          data-aos="fade-up"
          data-cms-key="terms.trademarksContent"
          sx={{
            mb: 3
          }}
        >
          {trademarksContent}
        </Typography>

        <Typography
          variant="h4"
          data-aos="fade-up"
          data-cms-key="terms.contentTitle"
          sx={{
            mb: 3
          }}
        >
          {contentTitle}
        </Typography>
        <Typography
          variant="body1"
          data-aos="fade-up"
          data-cms-key="terms.contentContent"
          sx={{
            mb: 3
          }}
        >
          {contentContent}
        </Typography>

        <Typography
          variant="h4"
          data-aos="fade-up"
          data-cms-key="terms.linkedWebsitesTitle"
          sx={{
            mb: 3
          }}
        >
          {linkedWebsitesTitle}
        </Typography>
        <Typography
          variant="body1"
          data-aos="fade-up"
          data-cms-key="terms.linkedWebsitesContent"
          sx={{
            mb: 3
          }}
        >
          {linkedWebsitesContent}
        </Typography>

        <Typography
          variant="h4"
          data-aos="fade-up"
          data-cms-key="terms.privacyPolicyTitle"
          sx={{
            mb: 3
          }}
        >
          {privacyPolicyTitle}
        </Typography>
        <Typography
          variant="body1"
          data-aos="fade-up"
          data-cms-key="terms.privacyPolicyContent"
        >
          {privacyPolicyContent}
        </Typography>
      </Container>
    </Box>
  );
}